import { z } from "zod";

/**
 * Stable machine-readable error codes. The HTTP status is derived from the code on the server;
 * clients branch on `code`, never on message text.
 */
export const ErrorCode = z.enum([
  "UNAUTHENTICATED",
  "FORBIDDEN",
  "BANNED",
  "NOT_FOUND",
  "VALIDATION_FAILED",
  "RATE_LIMITED",
  "DEPENDENCY_UNAVAILABLE",
  "FEATURE_DISABLED",
  "IDEMPOTENCY_CONFLICT",
  "PROPERTY_VERSION_CHANGED",
  "PROPERTY_TYPE_NOT_ELIGIBLE",
  "PROPERTY_NOT_ENTERABLE",
  "ROOM_FULL",
  "TICKET_INVALID",
  /** ADR-0036: the tab's realtime protocol version is missing or not accepted; reload the page. */
  "CLIENT_OUTDATED",
  "SESSION_CONTROL_TAKEN",
  "CHAT_RESTRICTED",
  "OUTBOUND_INTENT_INVALID",
  "EMAIL_NOT_VERIFIED",
  "INSUFFICIENT_COINS",
  "PLOT_LIMIT_REACHED",
  "PRICE_CHANGED",
  "PROPERTY_NOT_AVAILABLE",
  "HOME_REQUIRED",
  "ANCHOR_OCCUPIED",
  "STAGE_NOT_NEXT",
  // Dollar commerce (M4, ADR-0007)
  "QUOTE_EXPIRED",
  "QUOTE_STALE",
  "PROPERTY_RESERVED",
  "SELF_TAKEOVER",
  "RESERVATION_LIMIT",
  "ORDER_NOT_PAYABLE",
  "PAYMENT_PROVIDER_ERROR",
  /** ADR-0046: Stripe Tax is on; the quote needs the buyer's country (and, for US/CA, postal code). */
  "TAX_ADDRESS_REQUIRED",
  /** ADR-0046: Stripe Tax could not calculate the tax; no price was fixed and nothing was charged. */
  "TAX_CALCULATION_FAILED",
  /** ADR-0040: the case, refund or alert changed since the operator loaded it (already resolved, retried or acknowledged). */
  "FINANCE_STATE_CHANGED",
  // Advertiser operations (M5, ADR-0009)
  "UPLOAD_REJECTED",
  "CREATIVE_NOT_APPROVED",
  "CATEGORY_RESTRICTED",
  "BILLBOARD_DATES_UNAVAILABLE",
  // Sponsored tasks for coins (M6, ADR-0011)
  "OFFER_NOT_ELIGIBLE",
  "DAILY_CAP_REACHED",
  "ATTEMPT_IN_PROGRESS",
  "ATTEMPT_EXPIRED",
  "CLAIM_TOO_EARLY",
  "BUDGET_EXHAUSTED",
  "QUIZ_INCORRECT",
  "CAMPAIGN_STATE_INVALID",
  "PARTNER_SIGNATURE_INVALID",
  "PARTNER_EVENT_REJECTED",
  /** ADR-0014: this private message cannot be sent (guest, unverified, muted either way, recipient accepts none...). */
  "DM_NOT_ALLOWED",
  /** ADR-0037: the password given to confirm an account deletion is not the account's password. */
  "PASSWORD_INCORRECT",
  /** ADR-0039: this account holds a staff role but has not turned on two-step sign-in (TOTP); set it up on /account. */
  "MFA_REQUIRED",
  /**
   * ADR-0039: enter a current authenticator code first (POST /me/two-factor/step-up), then retry once: this session has
   * not proved the second factor yet, or the action is `dangerous` and the last confirmation is too old.
   */
  "STEP_UP_REQUIRED",
  /** ADR-0039: the authenticator or backup code given to confirm is not right (or was already used). */
  "MFA_CODE_INVALID",
  "INTERNAL",
]);
export type ErrorCode = z.infer<typeof ErrorCode>;

export const ApiErrorBody = z.object({
  error: z.object({
    code: ErrorCode,
    message: z.string(),
    requestId: z.string().optional(),
    retryable: z.boolean(),
    details: z.unknown().optional(),
  }),
});
export type ApiErrorBody = z.infer<typeof ApiErrorBody>;

export const HTTP_STATUS_BY_CODE: Record<ErrorCode, number> = {
  UNAUTHENTICATED: 401,
  FORBIDDEN: 403,
  BANNED: 403,
  NOT_FOUND: 404,
  VALIDATION_FAILED: 422,
  RATE_LIMITED: 429,
  DEPENDENCY_UNAVAILABLE: 503,
  FEATURE_DISABLED: 403,
  IDEMPOTENCY_CONFLICT: 409,
  PROPERTY_VERSION_CHANGED: 409,
  PROPERTY_TYPE_NOT_ELIGIBLE: 422,
  PROPERTY_NOT_ENTERABLE: 403,
  ROOM_FULL: 409,
  TICKET_INVALID: 401,
  /** A version conflict (docs/06 section 1: 409 for version/availability); retrying without a reload cannot help. */
  CLIENT_OUTDATED: 409,
  SESSION_CONTROL_TAKEN: 409,
  CHAT_RESTRICTED: 403,
  OUTBOUND_INTENT_INVALID: 404,
  EMAIL_NOT_VERIFIED: 403,
  INSUFFICIENT_COINS: 422,
  PLOT_LIMIT_REACHED: 422,
  PRICE_CHANGED: 409,
  PROPERTY_NOT_AVAILABLE: 409,
  HOME_REQUIRED: 422,
  ANCHOR_OCCUPIED: 409,
  STAGE_NOT_NEXT: 409,
  /** The quote passed its expiry: ask for a new quote. */
  QUOTE_EXPIRED: 409,
  /** Price, ownership or available credit changed since the quote: ask for a new quote. */
  QUOTE_STALE: 409,
  /** Another buyer holds an active reservation on this property. */
  PROPERTY_RESERVED: 409,
  /** The current owner cannot buy their own building. */
  SELF_TAKEOVER: 409,
  /** Too many active checkouts for this account. */
  RESERVATION_LIMIT: 429,
  /** The order is not in a state that can take a payment (expired, fulfilled, canceled...). */
  ORDER_NOT_PAYABLE: 409,
  /** The payment provider failed or timed out; safe to retry (idempotent). */
  PAYMENT_PROVIDER_ERROR: 502,
  /** Ask the buyer for a country and postal code, then quote again. */
  TAX_ADDRESS_REQUIRED: 422,
  /** Stripe Tax failed (unavailable, not set up, or the location is not recognised); no price was fixed. */
  TAX_CALCULATION_FAILED: 502,
  /** A finance staff action whose target already moved on (another operator acted first); reload, nothing was changed. */
  FINANCE_STATE_CHANGED: 409,
  /** The file is not an allowed image or breaks a size/pixel limit (the message says which). */
  UPLOAD_REJECTED: 422,
  /** Only an APPROVED creative of the right kind and owner can be placed or rented. */
  CREATIVE_NOT_APPROVED: 409,
  /** The advertiser category is restricted until the founder sets the policy (ADR-0009). */
  CATEGORY_RESTRICTED: 403,
  /** At least one requested UTC day is held or booked. */
  BILLBOARD_DATES_UNAVAILABLE: 409,
  /** The resident cannot start this sponsored task (already earned, own campaign, quiz failed, guest...). */
  OFFER_NOT_ELIGIBLE: 422,
  /** The resident's sponsored-coin allowance for this UTC day (or the offer's daily limit) is used up. */
  DAILY_CAP_REACHED: 422,
  /** Another sponsored task attempt of this account is still active (one at a time). */
  ATTEMPT_IN_PROGRESS: 409,
  /** The attempt expired (its budget and allowance were released); start again if still eligible. */
  ATTEMPT_EXPIRED: 409,
  /** The website task needs the link opened and the minimum time to pass (details.claimableAt). */
  CLAIM_TOO_EARLY: 409,
  /** The campaign has no budget left for another reward slot. */
  BUDGET_EXHAUSTED: 409,
  /** Wrong quiz answer (details.triesLeft; 0 ends the attempt). */
  QUIZ_INCORRECT: 422,
  /** The campaign cannot make this change in its current state (e.g. resume after a takeover). */
  CAMPAIGN_STATE_INVALID: 409,
  /** Unknown/revoked key, bad signature, timestamp outside the window, or a replayed nonce. */
  PARTNER_SIGNATURE_INVALID: 401,
  /** A correctly signed callback that does not match a live attempt of this partner (scope, type, expiry). */
  PARTNER_EVENT_REJECTED: 422,
  DM_NOT_ALLOWED: 403,
  PASSWORD_INCORRECT: 403,
  MFA_REQUIRED: 403,
  STEP_UP_REQUIRED: 403,
  MFA_CODE_INVALID: 403,
  INTERNAL: 500,
};
